import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { ReportItem } from '@/constants/ReportDashboard/report-items'
import {
  useAttributionLoading,
  useAttributionSecurityContribution,
  useAttributionSecurityError,
} from '@/contexts/FAAttributionDataContext/hooks'
import {
  useAttributionHoldingsControls,
  useAttributionTopBottom,
} from '@/contexts/AttributionHoldingsContext'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import FilterIndicatorBox from '@/components/ReportDashboard/Shared/FilterIndicatorBox'
import FilterListIcon from '@mui/icons-material/FilterList'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

type ViewMode = 'topBottom' | 'all'

// A security needs both axis values to be plottable; an incomplete one is omitted, not mis-placed.
const hasBothAxes = (d: IGfpaSecurityContrib) => d.security_ret != null && d.security_ret_contrib != null

// Return contribution bubble chart: X = Security Return (%), Y = Contribution to Return (bps), bubble
// size = Market Value ($M). Shares ranking (metric, count, show mode) with the Top/Bottom table so
// the two cards stay in sync.
const AttributionBubbleCard: React.FC<CardProps> = ({ cardId }) => {
  const theme = useTheme()
  const rows = useAttributionSecurityContribution()
  const loading = useAttributionLoading()
  const error = useAttributionSecurityError()
  const { showContributors, count, securitiesFiltered, highlightGroups, highlightedSecurities } = useAttributionHoldingsControls()
  const { topItems, bottomItems } = useAttributionTopBottom()
  const [viewMode, setViewMode] = useState<ViewMode>('topBottom')

  // All Holdings view honors a group selection by plotting the highlighted set, not every row.
  const allItems = useMemo(() => highlightedSecurities.filter(hasBothAxes), [highlightedSecurities])

  const chartOptions = useMemo((): Highcharts.Options => {
    const dataset = viewMode === 'all' ? allItems : [...topItems, ...bottomItems]
    const mvOf = (d: IGfpaSecurityContrib) => {
      const v = d[ReportItem.MarketValue]
      return v == null ? null : Number(v) / 1e6
    }
    const mvValues = dataset.map(mvOf).filter((v): v is number => v != null && Number.isFinite(v))
    const minMv = mvValues.length ? Math.min(...mvValues) : 0
    const maxMv = mvValues.length ? Math.max(...mvValues) : 0
    const minR = 6, maxR = 24
    const radiusFor = (mv: number | null): number => {
      if (mv == null || !Number.isFinite(mv)) return minR
      if (maxMv === minMv) return (minR + maxR) / 2
      return minR + ((mv - minMv) / (maxMv - minMv)) * (maxR - minR)
    }

    const makePoints = (data: IGfpaSecurityContrib[]) =>
      data
        .filter(hasBothAxes)
        .map((d) => {
          const mv = mvOf(d)
          return {
            x: Number(d.security_ret),
            // security_ret_contrib is in bps; plotted as-is to match the Top/Bottom table.
            y: Number(d.security_ret_contrib),
            name: d[ReportItem.Description] ?? d[ReportItem.StandardID] ?? '',
            custom: { identifier: d[ReportItem.StandardID], marketValue: mv },
            marker: { radius: radiusFor(mv) },
          }
        })

    const dataLabels = {
      enabled: true,
      format: '{point.name}',
      style: { fontSize: '13px', fontWeight: '500', color: theme.textPrimary, textOutline: 'none' },
    }

    const series: Highcharts.SeriesOptionsType[] = []
    if (viewMode === 'all') {
      const allPoints = makePoints(allItems)
      if (allPoints.length) {
        series.push({
          type: 'scatter',
          name: 'All Holdings',
          data: allPoints,
          color: getColorWithOpacity(COMPANY_COLORS.MEDIUM_BLUE, 0.55),
          marker: { symbol: 'circle', lineColor: COMPANY_COLORS.MEDIUM_BLUE, lineWidth: 1.5 },
          dataLabels,
        } as Highcharts.SeriesScatterOptions)
      }
    } else {
      const showTop = showContributors !== 'bottomOnly'
      const showBottom = showContributors !== 'topOnly'
      const topPoints = showTop ? makePoints(topItems) : []
      const bottomPoints = showBottom ? makePoints(bottomItems) : []
      if (topPoints.length) {
        series.push({
          type: 'scatter',
          name: `Top ${count}`,
          data: topPoints,
          color: getColorWithOpacity(COMPANY_COLORS.SUCCESS, 0.55),
          marker: { symbol: 'circle', lineColor: COMPANY_COLORS.SUCCESS, lineWidth: 1.5 },
          dataLabels,
        } as Highcharts.SeriesScatterOptions)
      }
      if (bottomPoints.length) {
        series.push({
          type: 'scatter',
          name: `Bottom ${count}`,
          data: bottomPoints,
          color: getColorWithOpacity(COMPANY_COLORS.ERROR, 0.45),
          marker: { symbol: 'circle', lineColor: COMPANY_COLORS.ERROR, lineWidth: 1.5 },
          dataLabels,
        } as Highcharts.SeriesScatterOptions)
      }
    }

    return {
      chart: {
        type: 'scatter',
        zooming: { type: 'xy' },
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        animation: false,
        spacing: [8, 16, 8, 8],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { color: theme.textPrimary, fontSize: '14px', fontWeight: '400' },
        itemHoverStyle: { color: theme.textPrimary },
      },
      xAxis: {
        title: { text: 'Security Return (%)', style: { color: theme.textPrimary, fontSize: '14px' } },
        labels: { style: { color: theme.textPrimary, fontSize: '14px' } },
        gridLineColor: theme.border,
        lineColor: theme.border,
        tickColor: theme.border,
      },
      yAxis: {
        title: { text: 'Contribution to Return (bps)', style: { color: theme.textPrimary, fontSize: '14px' } },
        labels: { style: { color: theme.textPrimary, fontSize: '14px' } },
        gridLineColor: theme.border,
      },
      tooltip: {
        useHTML: true,
        backgroundColor: theme.cardBackground,
        borderColor: theme.border,
        style: { color: theme.textPrimary, fontSize: '14px' },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const custom = (this.point.options.custom ?? {}) as { identifier?: string | null; marketValue?: number | null }
          const header = custom.identifier ? `${custom.identifier} - ${this.point.name}` : this.point.name
          const mv = custom.marketValue
          const mvLine = mv != null && Number.isFinite(mv) ? `<div>Market Value: <b>$${mv.toFixed(2)}M</b></div>` : ''
          return `<b>${header}</b>
            <div>Security Return: <b>${Number(this.x).toFixed(2)}%</b></div>
            <div>Contribution to Return: <b>${Number(this.y).toFixed(2)} bps</b></div>${mvLine}`
        },
      },
      plotOptions: { scatter: { marker: { states: { hover: { enabled: true } } } } },
      series,
    }
  }, [viewMode, theme, topItems, bottomItems, allItems, showContributors, count])

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: '12px' }}>
        <Stack>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
            {CardTitleLookup[cardId]} {securitiesFiltered ? <FilterIndicatorBox groups={highlightGroups} /> : null}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>Graphical representation of return contributors</Typography>
        </Stack>
        <Button
          size='small'
          startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
          onClick={() => setViewMode((m) => (m === 'all' ? 'topBottom' : 'all'))}
          sx={{
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: '6px',
            padding: '4px 10px',
            backgroundColor: 'transparent',
            color: theme.textPrimary,
            border: `1px solid ${theme.border}`,
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: theme.border + '44' },
          }}
        >
          {viewMode === 'all' ? 'All Holdings' : 'Top/Bottom'}
        </Button>
      </Stack>

      {error ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPANY_COLORS.ERROR }}>Couldn&apos;t load holdings</Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>There was an error fetching the data. Please retry, or reach out to support if it persists.</Typography>
        </Stack>
      ) : loading && (rows == null || rows.length === 0) ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: COMPANY_COLORS.DARK_BLUE }} />
        </Stack>
      ) : (rows == null || rows.length === 0) ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>Run analysis for the selected portfolio and date range to view holdings.</Typography>
        </Stack>
      ) : (chartOptions.series?.length ?? 0) === 0 ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>No securities have both a return and a contribution to plot.</Typography>
        </Stack>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            updateArgs={[true, true, false]}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        </Box>
      )}
    </Stack>
  )
}

export default AttributionBubbleCard
